import axios from 'axios';
import { auth } from '@/lib/firebase';

const API_URL = import.meta.env.VITE_API_URL;

const getAuthHeaders = async () => {
  const token = await auth.getIdToken();
  if (!token) {
    throw new Error('Not authenticated');
  }
  return {
    Authorization: `Bearer ${token}`,
  };
};

export const devContainerAPI = {
  /**
   * Create a new workspace
   */
  async createDevContainer(data: {
    name: string;
    ocpu?: number;
    memory?: number;
    storage?: number;
  }) {
    const headers = await getAuthHeaders();
    const response = await axios.post(
      `${API_URL}/api/dev-containers/workspaces`,
      data,
      { headers }
    );
    return response.data;
  },

  /**
   * Get all user workspaces
   */
  async getUserDevContainers() {
    const headers = await getAuthHeaders();
    const response = await axios.get(
      `${API_URL}/api/dev-containers/workspaces`,
      { headers }
    );
    return response.data;
  },

  /**
   * Get a specific dev container
   */
  async getDevContainer(devContainerId: string) {
    const headers = await getAuthHeaders();
    const response = await axios.get(
      `${API_URL}/api/dev-containers/workspaces/${devContainerId}`,
      { headers }
    );
    return response.data;
  },

  /**
   * Start a workspace
   */
  async startDevContainer(devContainerId: string) {
    const headers = await getAuthHeaders();
    const response = await axios.post(
      `${API_URL}/api/dev-containers/workspaces/${devContainerId}/start`,
      {},
      { headers }
    );
    return response.data;
  },

  /**
   * Stop a workspace
   */
  async stopDevContainer(devContainerId: string) {
    const headers = await getAuthHeaders();
    const response = await axios.post(
      `${API_URL}/api/dev-containers/workspaces/${devContainerId}/stop`,
      {},
      { headers }
    );
    return response.data;
  },

  /**
   * Delete a workspace
   */
  async deleteDevContainer(devContainerId: string) {
    const headers = await getAuthHeaders();
    const response = await axios.delete(
      `${API_URL}/api/dev-containers/workspaces/${devContainerId}`,
      { headers }
    );
    return response.data;
  },

  /**
   * Clone a project to a workspace
   */
  async cloneProjectToWorkspace(devContainerId: string, projectId: string) {
    const headers = await getAuthHeaders();
    const response = await axios.post(
      `${API_URL}/api/dev-containers/workspaces/${devContainerId}/clone-project`,
      { projectId },
      { headers }
    );
    return response.data;
  },

  /**
   * Get workspace access URL
   */
  async getDevContainerAccessUrl(devContainerId: string) {
    const headers = await getAuthHeaders();
    const response = await axios.get(
      `${API_URL}/api/dev-containers/workspaces/${devContainerId}/access-url`,
      { headers }
    );
    return response.data;
  },

  /**
   * Get service-specific access URL with token
   */
  async getServiceAccessUrl(devContainerId: string, service: string) {
    const headers = await getAuthHeaders();
    const response = await axios.get(
      `${API_URL}/api/dev-containers/workspaces/${devContainerId}/access-url?service=${service}`,
      { headers }
    );
    return response.data;
  },

  /**
   * Validate workspace access
   */
  async validateAccess(subdomain: string) {
    const headers = await getAuthHeaders();
    const response = await axios.post(
      `${API_URL}/api/dev-containers/validate-access`,
      { subdomain },
      { headers }
    );
    return response.data;
  },

  /**
   * Send heartbeat to track active usage.
   * Updates lastAccessedAt to prevent idle pause, and returns the current
   * container status so callers can detect out-of-band pauses.
   * `healthy` reflects K8s deployment readiness (30s-cached server side):
   * false while status is "running" means the workspace pod is unreachable
   * (e.g. node failure). null when not applicable or unknown.
   */
  async heartbeat(
    devContainerId: string,
  ): Promise<{ success: boolean; status: string | null; healthy?: boolean | null }> {
    const headers = await getAuthHeaders();
    const response = await axios.post(
      `${API_URL}/api/dev-containers/workspaces/${devContainerId}/heartbeat`,
      {},
      { headers }
    );
    return response.data;
  },
};